import { gql } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Switch,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState, MouseEvent } from "react";
import { formatISO, isBefore } from "date-fns";

import { Permission } from "~/hooks/permissions";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import StartEndTime from "~/components/common/forms/StartEndTime";

import { ShiftProviderTravelEnum, ShiftStatusEnum, useUpdateShiftMutation, useUpdateShiftStatusMutation } from "~/graphql/__generated__/graphql";
import { LocalShift } from "~/hooks/visitDragDropReducer";
import { ShiftCenter } from "~/providers/OrgConfigProvider";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { timezoneAbbreviation } from "@caredna-marlin/utils/dates";
import Help from "../common/Help";

type Props = {
  shift: LocalShift;
  shiftCenters: ShiftCenter[];
  onClose: () => void;
};

const LocationChip = ({ shift }: { shift: LocalShift }) => {
  return shift.providerTravel === "inHomeOnly" ? (
    <Chip label="In-Home" color="primary" size="small" />
  ) : shift.providerTravel === "virtualOnly" ? (
    <Chip label="Virtual" color="secondary" size="small" />
  ) : (
    <Chip label="In-Home or Virtual" color="default" size="small" />
  )
}

export default function ShiftPopup({
  shift,
  shiftCenters,
  onClose,
}: Props) {
  const [updateShift, { loading }] = useUpdateShiftMutation({ refetchQueries: 'active', awaitRefetchQueries: true });
  const [cancelRestoreShift, { loading: cancelRestoreLoading }] = useUpdateShiftStatusMutation({ refetchQueries: 'active', awaitRefetchQueries: true });

  const [localEdits, setLocalEdits] = useState<boolean>(false);
  const [startTime, setStartTime] = useState<Date>(new Date(shift.startTime));
  const [endTime, setEndTime] = useState<Date>(new Date(shift.endTime));
  const [providerTravel, setProviderTravel] = useState<ShiftProviderTravelEnum>(shift.providerTravel);
  const [shiftCenterId, setShiftCenterId] = useState<number | null>(shift.shiftCenterId || null);
  const [overflowShift, setOverflowShift] = useState<boolean>(shift.overflowShift);
  const [useShiftCenterDriveTimes, setUseShiftCenterDriveTimes] = useState<boolean>(shift.useShiftCenterDriveTimes);
  const [error, setError] = useState<string | null>(null);

  const { timeRangeLabel } = useZonedDateFormat();

  // https://mui.com/material-ui/react-menu/
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const shiftLength = endTime.getTime() - startTime.getTime();
  const shiftTooShort = shiftLength < 3600 * 1000;

  const onEditSave = async () => {
    if (!localEdits) {
      setLocalEdits(true);
      return;
    }

    if (!startTime || !endTime) {
      setError("Start time and end time must both be set.");
      return;
    }

    if (!isBefore(startTime, endTime)) {
      setError("Start time must be before end time.");
      return;
    }

    setError(null); // Clear any previous error message
    try {
      await updateShift({
        variables: {
          id: shift.id,
          startTime: formatISO(startTime),
          endTime: formatISO(endTime),
          originalStartTime: formatISO(new Date(shift.startTime)),
          originalEndTime: formatISO(new Date(shift.endTime)),
          providerTravel,
          shiftCenterId,
          overflowShift,
          useShiftCenterDriveTimes,
        },
        refetchQueries: ["CommandIndexCombinedData"],
        awaitRefetchQueries: true,
      });
      onClose(); // Close popover on success
    } catch (error) {
      console.error("Failed to update shift:", error);
      setError("Something went wrong. Please try again.");
    }
  };

  const onCancel = async () => {
    // We cancel rather than delete shifts from the command center because once cancelled,
    // they still exist (with manualEdits=true) and block the availability management flow
    // from re-creating them.
    await cancelRestoreShift({
      variables: { id: shift.id, status: ShiftStatusEnum["Canceled"] },
    });
    onClose();
  };

  const onConfirm = async () => {
    await cancelRestoreShift({
      variables: { id: shift.id, status: ShiftStatusEnum["Confirmed"] },
    });
  };

  const time = <Typography variant="body1" gutterBottom component="div">{
    localEdits ? <StartEndTime
      mode="datetime"
      startTime={startTime}
      endTime={endTime}
      onChange={(start, end) => { setStartTime(start); setEndTime(end); }}
      timezone={shift.addressTimezone ?? undefined}
    /> : <>
      {timeRangeLabel(shift.startTime, shift.endTime, false, false, shift.addressTimezone ?? undefined)}{' '}
      {shift.addressTimezone && timezoneAbbreviation(shift.addressTimezone)}
    </>
  }</Typography>


  const inHomeOrVirtualChip = !localEdits ? <>
    <LocationChip shift={shift} />
    {shift.overflowShift && <Chip sx={{ ml: 2 }} label="Overflow Shift" color="default" size="small" />}
  </> : (
    <FormControl sx={{ width: "100%" }}>
      <InputLabel id="providerTravelSelect">Visits</InputLabel>
      <Select
        size="small"
        label="ScheduleVisits"
        value={providerTravel}
        onChange={(e) => setProviderTravel(e.target.value as ShiftProviderTravelEnum)}
      >
        <MenuItem value="inHomeOnly">In-Home</MenuItem>
        <MenuItem value="virtualOnly">Virtual</MenuItem>
        <MenuItem value="either">In-Home or Virtual</MenuItem>
      </Select>
    </FormControl>
  )

  const currentShiftCenter = shiftCenterId ? shiftCenters?.find((s) => s.id === shiftCenterId) : null;
  const currentShiftCenterName = shiftCenterId && currentShiftCenter?.id ? currentShiftCenter?.name : "Provider home"

  const shiftCenter = localEdits ? (
    <FormControl sx={{ width: "100%" }}>
      <InputLabel id="shiftCenterSelect">Shift Center</InputLabel>
      <Select
        label="Shift Center"
        size="small"
        id="shiftCenterId"
        value={shiftCenterId || "none"}
        onChange={(event) => setShiftCenterId(event.target.value ? Number(event.target.value) : null)}
        sx={{ maxWidth: "20em", minWidth: "6em" }}
      >
        <MenuItem value="none">
          <em>Provider home</em>
        </MenuItem>
        {Object.values(shiftCenters).map((center) => (
          <MenuItem key={center.id} value={center.id}>
            {center.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : (<>
    <Typography variant="button">Shift Center</Typography>
    <Typography variant="body1" gutterBottom sx={{ maxWidth: "20em" }}>{currentShiftCenterName}{(useShiftCenterDriveTimes && shiftCenterId) ? " (using for drive times)": ""}</Typography>
  </>)

  const driveTimes = localEdits ? <>
    {!!shiftCenterId && <FormControlLabel
      label={<>Use for drive times <Help text="The shift center will be used to calculate star/end of shift drive times, rather than the provider's home address"/></>}
      control={
        <Switch
          name="driveTimes"
          checked={useShiftCenterDriveTimes}
          onChange={(e) => setUseShiftCenterDriveTimes(e.target.checked)}
        />
      }
    />}
  </> : null

  const overflowShiftField = localEdits ? (
    <FormControlLabel
      label={<>Overflow Shift <Help text="The scheduler screen will only suggest using overflow shifts for prioritized visit types where the scheduler requests it"/></>}
      control={
        <Switch
          name="overflowShift"
          checked={overflowShift}
          onChange={(e) => setOverflowShift(e.target.checked)}
        />
      }
    />
  ) : null;

  return (
    <Card elevation={3} sx={{ width: 600 }}>
      <Header shift={shift} onClose={onClose} />
      <CardContent>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <Grid container direction="row" spacing={2} sx={{ mt: 3 }}>
          <Grid size={12}>
            {!localEdits && <Typography variant="button">{shift.status} Shift</Typography>}
            {time}
            {shiftTooShort && <>
              <WarningAmberRoundedIcon color="error" sx={{ verticalAlign: "bottom", mr: 0.25 }} /> Shifts must be at least one hour long
            </>}
          </Grid>
          <Grid size={5}>
            {inHomeOrVirtualChip}
          </Grid>
          <Grid size={7}>
            {overflowShiftField}
          </Grid>
          <Grid size={5}>
            {shiftCenter}
          </Grid>
          <Grid size={7}>
            {driveTimes}
          </Grid>
        </Grid>
      </CardContent>
      <PermissionOrHide permission={Permission.manageProviderShifts}>
        <CardActions sx={{ justifyContent: "end", borderTop: 1, borderColor: "divider" }}>
          {shift.status === ShiftStatusEnum["Confirmed"] && <>
            <Button color="error" onClick={handleClick} loading={cancelRestoreLoading} disabled={loading}>
              Cancel Shift
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              slotProps={{ list: { 'aria-labelledby': 'basic-button' }}}
            >
              <MenuItem disabled={loading || cancelRestoreLoading} onClick={onCancel}>
                <ListItemText>Confirm Cancel</ListItemText>
              </MenuItem>
            </Menu>
          </>}
          {shift.status === ShiftStatusEnum["Canceled"] && <>
            <Button color="primary" onClick={onConfirm} loading={cancelRestoreLoading} disabled={loading}>
              Restore Shift
            </Button>
          </>}
          <Button color="primary" onClick={onEditSave} disabled={shiftTooShort || cancelRestoreLoading} loading={loading}>
            {localEdits ? "Save" : "Edit"}
          </Button>
        </CardActions>
      </PermissionOrHide>
    </Card>
  );
}

const Header = ({ shift, onClose }: { shift: Props["shift"], onClose: () => void }) => (<CardHeader
  sx={{
    borderBottom: 1,
    borderColor: "divider",
    flex: 1,
    flexDirection: "row",
  }}
  action={<IconButton aria-label="close" onClick={onClose}>
    <Close />
  </IconButton>}
  title={<Typography variant="h6">
    {shift.provider?.firstName} {shift.provider?.lastName}
    {shift.manualEdits && <Tooltip title={`Availability will not override Command Center shift edits.`}>
      <Chip label="Manual Shift" color="warning" size="small" sx={{ cursor: "help", ml: 1.5, verticalAlign: "text-top" }}/>
    </Tooltip>}
  </Typography>}
/>);

gql(`
  mutation UpdateShift(
    $id: Int!
    $startTime: String
    $endTime: String
    $originalStartTime: String
    $originalEndTime: String
    $providerTravel: ShiftProviderTravelEnum
    $shiftCenterId: Int
    $overflowShift: Boolean
    $useShiftCenterDriveTimes: Boolean
  )
  @permission(requires: manageProviderShifts)
  {
    updateShift(
      id: $id
      startTime: $startTime
      endTime: $endTime
      originalStartTime: $originalStartTime
      originalEndTime: $originalEndTime
      providerTravel: $providerTravel
      shiftCenterId: $shiftCenterId
      overflowShift: $overflowShift
      useShiftCenterDriveTimes: $useShiftCenterDriveTimes
    ) {
      id
      startTime
      endTime
      providerTravel
      overflowShift
      shiftCenterId
    }
  }
`);

gql(`
  mutation UpdateShiftStatus(
    $id: Int!
    $status: ShiftStatusEnum
  )
  @permission(requires: manageProviderShifts)
  {
    updateShift(
      id: $id
      status: $status
    ) {
      id
      status
    }
  }
`);
